import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import type { IPayslipPreview } from "@/types/(company)/payrollRuns.interface";
import { formatDateDmy } from "@/utils/helper";
import { useLocale } from "@/hooks/useLocale";

/**
 * A period bound is a calendar date, not an instant. `new Date("2026-09-10")`
 * is UTC midnight, which renders as the 9th anywhere west of UTC — parse the
 * date part as local midnight instead.
 */
const formatPeriodDay = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = parseISO(iso.slice(0, 10));
  return isNaN(d.getTime()) ? null : formatDateDmy(d);
};

/**
 * "Period" and "Paid Days" rows for a payslip's details grid.
 *
 * The period starts on the employee's joining date when they joined mid-period
 * (the API resolves that), so a joiner's payslip no longer shows days before
 * they started. Both rows are omitted when the API sends no working days —
 * final settlements, which pay exit items rather than a period.
 */
export const PayslipPeriodRows = ({
  preview,
}: {
  preview: IPayslipPreview;
}) => {
  const { t } = useTranslation(["company"]);
  const { formatNumber } = useLocale();
  const workingDays = Number(preview.workingDays ?? 0);
  if (!workingDays) return null;
  // "22", never "22.00" (B39).
  const days = (n: number) => formatNumber(n, { maximumFractionDigits: 2 });
  const paid = Number(preview.paidDays ?? 0);
  // A joiner or leaver is paid against the days they were employed, not the
  // whole period: "5 of 23" read as 18 days missed (B39).
  const employed = Number(preview.employedDays ?? workingDays);
  const partPeriod = employed < workingDays;
  const from = formatPeriodDay(preview.periodStart);
  const to = formatPeriodDay(preview.periodEnd);
  return (
    <>
      {from && to && (
        <>
          <span className="text-gray-500 text-start">
            {t("company:payslip.period_colon")}
          </span>
          <span className="font-semibold text-gray-900 text-end">
            {t("company:payslip.period_range", { from, to })}
          </span>
        </>
      )}
      <span className="text-gray-500 text-start">
        {t("company:payslip.paid_days_colon")}
      </span>
      <span className="font-semibold text-gray-900 text-end">
        {partPeriod
          ? t("company:payslip.paid_days_of_employed", {
              paid: days(paid),
              employed: days(employed),
              working: days(workingDays),
            })
          : t("company:payslip.paid_days_of_working", {
              paid: days(paid),
              working: days(workingDays),
            })}
      </span>
    </>
  );
};
